import React, {useLayoutEffect, useRef} from 'react';
import {IElementQuestionMatchPair, IMatchPairQuestion} from '../../../../../incase-domain/elements/question/questionMatchPair';
import {CORRECT_ANSWER_COLOR, INCORRECT_ANSWER_COLOR, MATCH_PAIR_SELECTED_COLOR} from '../../../../../incase-domain/common/color';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {checkIfPairIsCorrect} from '../../../../../util/matchPairs.utils';
import {getBorderStyle} from '../../../../../util/style.utils';
import {getElementScale} from '../../../../../util/domScale';
import {colors} from './MatchPairQuestionElements';
import {Pair} from './MatchPairQuestionElement';

type CardState = 'selected' | 'paired' | 'correct' | 'incorrect';

interface MatchPairQuestionCardsProps {
    element: IElementQuestionMatchPair;
    selectedPairs: Pair[];
    answered: boolean;
    wrongAnswer?: Pair;
    handleItemClick: (id: string, position: keyof Pair) => void;
    device?: DeviceType;
}

interface PairGroup {
    right: IMatchPairQuestion;
    lefts: IMatchPairQuestion[];
}

interface CardPlacement {
    item: IMatchPairQuestion;
    position: keyof Pair;
    row: number;
    span: number;
}

const uniqueByText = (items: IMatchPairQuestion[]): IMatchPairQuestion[] =>
    items.filter((item, index, self) => self.findIndex((t) => t.text === item.text) === index);

const isComplete = (pair: Pair): boolean => !!pair.leftSelectedElement && !!pair.rightSelectedElement;

const MAX_CARD_PADDING_EM = 1;
const MIN_CARD_PADDING_EM = 0.375;

const fitCards = (container: HTMLDivElement): void => {
    const setPadding = (em: number): void => container.style.setProperty('--matchPairCardPaddingBlock', `${em}em`);
    container.classList.remove('c-matchPairQuestionCards--scroll');

    setPadding(MAX_CARD_PADDING_EM);
    const available = container.clientHeight;
    const maxHeight = container.scrollHeight;
    if (maxHeight <= available + 1) return;

    setPadding(MIN_CARD_PADDING_EM);
    const minHeight = container.scrollHeight;
    if (minHeight > available + 1) {
        container.classList.add('c-matchPairQuestionCards--scroll');
        return;
    }

    const ratio = (available - minHeight) / (maxHeight - minHeight);
    setPadding(Math.floor((MIN_CARD_PADDING_EM + ratio * (MAX_CARD_PADDING_EM - MIN_CARD_PADDING_EM)) * 1000) / 1000);
};

function groupPairs(left: IMatchPairQuestion[], right: IMatchPairQuestion[], pairs: Pair[]): PairGroup[] {
    const groups: PairGroup[] = [];
    pairs.filter(isComplete).forEach(({leftSelectedElement, rightSelectedElement}) => {
        const leftItem = left.find((item) => item.id === leftSelectedElement);
        const rightItem = right.find((item) => item.id === rightSelectedElement);
        if (!leftItem || !rightItem || groups.some((group) => group.lefts.includes(leftItem))) return;

        const group = groups.find((existing) => existing.right === rightItem);
        if (group) group.lefts.push(leftItem);
        else groups.push({right: rightItem, lefts: [leftItem]});
    });
    return groups;
}

function placeCards(left: IMatchPairQuestion[], right: IMatchPairQuestion[], groups: PairGroup[]): CardPlacement[] {
    const placements: CardPlacement[] = [];
    let row = 0;

    groups.forEach((group) => {
        group.lefts.forEach((item, index) => placements.push({item, position: 'leftSelectedElement', row: row + index * 2, span: 2}));
        placements.push({item: group.right, position: 'rightSelectedElement', row, span: group.lefts.length * 2});
        row += group.lefts.length * 2;
    });

    const freeLeft = left.filter((item) => !groups.some((group) => group.lefts.includes(item)));
    const freeRight = right.filter((item) => !groups.some((group) => group.right === item));
    const freeRows = Math.max(freeLeft.length, freeRight.length);
    const placeFree = (items: IMatchPairQuestion[], position: keyof Pair): void =>
        items.forEach((item, index) => placements.push({item, position, row: row + freeRows - items.length + index * 2, span: 2}));

    placeFree(freeLeft, 'leftSelectedElement');
    placeFree(freeRight, 'rightSelectedElement');
    return placements;
}

function MatchPairQuestionCards(props: MatchPairQuestionCardsProps): JSX.Element {
    const {element, selectedPairs, answered, wrongAnswer, handleItemClick, device} = props;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const cellPositions = useRef<Map<string, {x: number; y: number}>>(new Map());
    const previousLayoutKey = useRef('');

    const leftItems = uniqueByText(element.leftElement);
    const rightItems = uniqueByText(element.rightElement);
    const isOneToOne =
        leftItems.length === rightItems.length && new Set(element.pairs.map((pair) => pair.rightElementId)).size === element.pairs.length;
    const isCorrectPair = (leftId: string, rightId: string): boolean => checkIfPairIsCorrect(element, leftId, rightId);

    const groups = groupPairs(leftItems, rightItems, selectedPairs);
    if (answered) {
        const isGroupCorrect = (group: PairGroup): boolean => group.lefts.every((item) => isCorrectPair(item.id, group.right.id));
        groups.forEach((group) =>
            group.lefts.sort((a, b) => Number(isCorrectPair(b.id, group.right.id)) - Number(isCorrectPair(a.id, group.right.id)))
        );
        groups.sort((a, b) => Number(isGroupCorrect(b)) - Number(isGroupCorrect(a)));
    }
    const placements = placeCards(leftItems, rightItems, groups);
    const layoutKey = placements.map(({item, position, row, span}) => `${position}-${item.id}:${row}:${span}`).join();

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const layoutChanged = previousLayoutKey.current !== '' && previousLayoutKey.current !== layoutKey;
        previousLayoutKey.current = layoutKey;

        fitCards(container);

        const containerRect = container.getBoundingClientRect();
        const {scaleX, scaleY} = getElementScale(container, undefined, undefined, containerRect);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        cellRefs.current.forEach((cell, key) => {
            const rect = cell.getBoundingClientRect();
            const next = {
                x: (rect.left - containerRect.left) * scaleX,
                y: (rect.top - containerRect.top) * scaleY + container.scrollTop,
            };
            const previous = cellPositions.current.get(key);
            cellPositions.current.set(key, next);

            if (!layoutChanged || reduceMotion || !previous || (previous.x === next.x && previous.y === next.y)) return;

            cell.animate([{transform: `translate(${previous.x - next.x}px, ${previous.y - next.y}px)`}, {transform: 'none'}], {
                duration: 350,
                delay: 250,
                easing: 'ease-in-out',
                fill: 'backwards',
            });
        });
    });

    const isWrong = (id: string): boolean => id === wrongAnswer?.leftSelectedElement || id === wrongAnswer?.rightSelectedElement;

    const getCardState = (id: string): {state?: CardState; color?: string} => {
        if (isWrong(id)) {
            return {state: 'incorrect', color: element.incorrectColor || INCORRECT_ANSWER_COLOR};
        }

        const isSelected = selectedPairs.some(
            (pair) => !isComplete(pair) && (pair.leftSelectedElement === id || pair.rightSelectedElement === id)
        );
        if (isSelected && !answered) {
            return {state: 'selected', color: element.selectedColor || MATCH_PAIR_SELECTED_COLOR};
        }

        const pairs = selectedPairs.filter(
            (pair) => isComplete(pair) && (pair.leftSelectedElement === id || pair.rightSelectedElement === id)
        );
        if (!pairs.length) return {};

        if (answered || !element.haveTryButton) {
            const isCorrect = pairs.every((pair) => isCorrectPair(pair.leftSelectedElement, pair.rightSelectedElement));
            return isCorrect
                ? {state: 'correct', color: element.correctColor || CORRECT_ANSWER_COLOR}
                : {state: 'incorrect', color: element.incorrectColor || INCORRECT_ANSWER_COLOR};
        }

        const groupIndex = groups.findIndex((group) => group.right.id === id || group.lefts.some((item) => item.id === id));
        return {state: 'paired', color: colors[groupIndex % colors.length]};
    };

    const isDisabled = (id: string, position: keyof Pair): boolean => {
        if (answered) return true;
        if (element.haveTryButton) return false;

        if (position === 'leftSelectedElement') {
            return selectedPairs.some((pair) => pair.leftSelectedElement === id && isCorrectPair(id, pair.rightSelectedElement));
        }

        const rightPairs = element.pairs.filter((pair) => pair.rightElementId === id);
        return (
            rightPairs.length > 0 &&
            rightPairs.every((pair) =>
                selectedPairs.some(
                    (selected) => selected.leftSelectedElement === pair.leftElementId && selected.rightSelectedElement === id
                )
            )
        );
    };

    const handleCardClick = (id: string, position: keyof Pair): void => {
        const pairedLeftId =
            isOneToOne && element.haveTryButton && position === 'rightSelectedElement'
                ? groups.find((group) => group.right.id === id)?.lefts[0]?.id
                : undefined;

        if (pairedLeftId) {
            handleItemClick(pairedLeftId, 'leftSelectedElement');
        } else {
            handleItemClick(id, position);
        }
    };

    return (
        <div
            ref={containerRef}
            className={`c-matchPairQuestionCards ${device === DeviceType.MOBILE ? 'c-matchPairQuestionCards--mobile' : ''}`}
        >
            {placements.map(({item, position, row, span}) => {
                const key = `${position}-${item.id}`;
                const {state, color} = getCardState(item.id);
                const isLinked = position === 'leftSelectedElement' && groups.some((group) => group.lefts.includes(item));

                return (
                    <div
                        key={key}
                        ref={(el) => {
                            if (el) cellRefs.current.set(key, el);
                            else cellRefs.current.delete(key);
                        }}
                        className="c-matchPairQuestionCards__cell"
                        style={{gridColumn: position === 'leftSelectedElement' ? 1 : 2, gridRow: `${row + 1} / span ${span}`}}
                    >
                        <button
                            type="button"
                            className={`c-matchPairQuestionCards__card ${state ? `c-matchPairQuestionCards__card--${state}` : ''}`}
                            style={
                                {
                                    '--matchPairCardColor': color,
                                    borderRadius: getBorderStyle(element.borderRadius),
                                    fontFamily: element.fontFamily?.name,
                                    fontSize: `${element.fontSize || 'initial'}em`,
                                    textAlign: element.answerTextAlign,
                                } as React.CSSProperties
                            }
                            disabled={isDisabled(item.id, position)}
                            onClick={() => handleCardClick(item.id, position)}
                        >
                            <span className="c-matchPairQuestionCards__text">{item.text}</span>
                        </button>
                        {isLinked && <span className="c-matchPairQuestionCards__link" style={{backgroundColor: color}} />}
                    </div>
                );
            })}
        </div>
    );
}

export default MatchPairQuestionCards;
